import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bot, Plus, Send, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input, Label, Textarea } from '@/components/ui/input';
import { Spinner } from '@/components/ui/states';
import { api } from '@/lib/api';
import { CHANNEL_LABELS, type InboxChannel } from './api';

/** Trợ lý ngoài giờ (CR-014 bước 5, DEC-231; bản 2 10/10/2026): cài đặt cho GĐ, khung giờ của NV, nháp báo giá chờ NV trong hội thoại. */
export interface ReplyWindow {
  start: string;
  end: string;
  /** 1 = T2 … 7 = CN; không có = mọi ngày */
  days?: number[];
}
export interface BotSettings {
  enabled: boolean;
  autoQuote: boolean;
  maxAutoTotal: number;
  workStart: string;
  workEnd: string;
  workDays: number[];
  delayMinutes: number;
  channels: InboxChannel[];
  /** Bước 7b (DEC-233): tóm tắt công việc cuối ngày */
  summaryEnabled: boolean;
  summaryTime: string;
  /** Bản 2: giọng, câu cố định khi tin không liên quan hàng, hotline, khung giờ trả lời, sổ tay, trần AI / ngày */
  tone: 'friendly' | 'formal';
  offTopicText: string;
  hotline: string;
  replyWindows: ReplyWindow[];
  skillNotes: string;
  aiDailyLimit: number;
  /** Tên Zalo chứa 1 trong các từ này = nick nội bộ công ty, trợ lý không trả lời */
  internalNameKeywords: string[];
  updatedAt: string;
  updatedByName: string | null;
}
type TemplateKind = 'media' | 'request' | 'quote' | 'rest' | 'order' | 'closing';
type BotTemplates = Record<TemplateKind, string>;
const TEMPLATE_KINDS: TemplateKind[] = ['media', 'request', 'quote', 'rest', 'order', 'closing'];
interface MyBotSettings {
  enabled: boolean;
  windows: ReplyWindow[] | null;
  /** Ngữ điệu NV tự tả + câu mẫu riêng (null = mẫu chung), 15/10/2026 */
  tonePrompt: string;
  templates: BotTemplates | null;
  defaultTemplates: BotTemplates;
  labels: Record<TemplateKind, string>;
  companyWindows: ReplyWindow[];
  effective: ReplyWindow[];
  companyEnabled: boolean;
}
interface BotReply {
  id: number;
  kind: 'ack' | 'quote' | 'info' | 'skip';
  status: 'sent' | 'draft' | 'done' | 'dismissed' | 'failed' | 'skipped';
  text: string;
  total: number | null;
  reason: string | null;
  error: string | null;
  createdAt: string;
}

const errMsg = (e: unknown) => (e instanceof Error ? e.message : 'Có lỗi, thử lại sau');
const DAYS: [number, string][] = [
  [1, 'T2'],
  [2, 'T3'],
  [3, 'T4'],
  [4, 'T5'],
  [5, 'T6'],
  [6, 'T7'],
  [7, 'CN'],
];

const DAY_SHORT = Object.fromEntries(DAYS) as Record<number, string>;
function windowText(w: ReplyWindow) {
  const d = w.days ?? [];
  if (!d.length || d.length === 7) return `${w.start}–${w.end}`;
  const contiguous = d.every((x, i) => i === 0 || x === d[i - 1]! + 1);
  const label = d.length > 2 && contiguous ? `${DAY_SHORT[d[0]!]}–${DAY_SHORT[d[d.length - 1]!]}` : d.map((x) => DAY_SHORT[x]).join(', ');
  return `${label} ${w.start}–${w.end}`;
}
const windowsText = (w: ReplyWindow[]) => (w.length ? w.map(windowText).join(', ') : 'không có');

/** Sửa danh sách khung giờ (trưa, tối…). */
function WindowsEditor({ value, onChange, label }: { value: ReplyWindow[]; onChange: (w: ReplyWindow[]) => void; label: string }) {
  const setAt = (i: number, p: Partial<ReplyWindow>) => onChange(value.map((w, k) => (k === i ? { ...w, ...p } : w)));
  return (
    <div className="space-y-1">
      {value.map((w, i) => (
        <div key={i} className="flex flex-wrap items-center gap-1.5">
          <Input type="time" className="h-8 w-28" value={w.start} onChange={(e) => setAt(i, { start: e.target.value })} aria-label={`${label} ${i + 1}: từ`} />
          <span className="text-xs text-muted-foreground">đến</span>
          <Input type="time" className="h-8 w-28" value={w.end} onChange={(e) => setAt(i, { end: e.target.value })} aria-label={`${label} ${i + 1}: đến`} />
          <span className="flex flex-wrap gap-0.5" role="group" aria-label={`${label} ${i + 1}: ngày áp dụng`}>
            {DAYS.map(([d, name]) => {
              const days = w.days ?? DAYS.map(([x]) => x);
              const on = days.includes(d);
              return (
                <button
                  key={d}
                  type="button"
                  aria-pressed={on}
                  className={`rounded px-1 text-[11px] ${on ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}
                  onClick={() => {
                    const next = on ? days.filter((x) => x !== d) : [...days, d].sort();
                    if (next.length) setAt(i, { days: next.length === 7 ? undefined : next });
                  }}
                >
                  {name}
                </button>
              );
            })}
          </span>
          <button
            type="button"
            className="rounded p-1 text-muted-foreground hover:bg-muted disabled:opacity-40"
            disabled={value.length <= 1}
            onClick={() => onChange(value.filter((_, k) => k !== i))}
            aria-label={`Bỏ ${label.toLowerCase()} ${i + 1}`}
          >
            <X className="size-3.5" />
          </button>
        </div>
      ))}
      {value.length < 8 && (
        <button
          type="button"
          className="flex items-center gap-1 text-xs text-primary hover:underline"
          onClick={() => onChange([...value, { start: '17:30', end: '21:00', days: [1, 2, 3, 4, 5, 6] }])}
        >
          <Plus className="size-3" /> Thêm khung giờ
        </button>
      )}
    </div>
  );
}

export function BotSettingsSection() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['zalo', 'bot-settings'], queryFn: () => api.get<BotSettings>('/zalo/bot/settings') });
  const [f, setF] = useState<BotSettings | null>(null);
  const [kw, setKw] = useState('');
  useEffect(() => {
    if (q.data) {
      setF(q.data);
      setKw((q.data.internalNameKeywords ?? []).join(', '));
    }
  }, [q.data]);
  const save = useMutation({
    mutationFn: (b: BotSettings) => {
      const { updatedAt: _u, updatedByName: _n, ...body } = b;
      return api.put<BotSettings>('/zalo/bot/settings', body);
    },
    onSuccess: (d) => {
      qc.setQueryData(['zalo', 'bot-settings'], d);
      toast.success(d.enabled ? 'Đã bật trợ lý ngoài giờ' : 'Đã lưu, trợ lý đang tắt');
    },
    onError: (e) => toast.error(errMsg(e)),
  });
  if (!f) return <Spinner />;
  const set = (p: Partial<BotSettings>) => setF({ ...f, ...p });
  const toggle = <T,>(arr: T[], v: T) => (arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);
  return (
    <div className="space-y-2.5 border-t pt-3">
      <div className="flex items-center justify-between gap-2">
        <span className="flex items-center gap-1.5 text-sm">
          <Bot className="size-4" /> Trợ lý ngoài giờ
        </span>
        <label className="flex items-center gap-1.5 text-sm">
          <input type="checkbox" checked={f.enabled} onChange={(e) => set({ enabled: e.target.checked })} aria-label="Bật trợ lý ngoài giờ" /> Bật
        </label>
      </div>
      <p className="text-xs text-muted-foreground">
        Trong khung giờ trả lời, khách nhắn mà chưa ai trả lời thì trợ lý báo giá phần tìm được giá (giá do ERP tính), khách gửi ảnh / tệp thì báo đã nhận và hẹn
        NV báo giá. Mặt hàng trợ lý không biết / chưa có giá thì im lặng, để nháp cho NV. Câu báo đã nhận chỉ gửi 1 lần / 12 giờ. Giọng nghiêm túc, không
        emoji. Phụ kiện khách không ghi loại thì hiểu là hàn đen SCH20. Việc khác (công nợ, hóa đơn, hỏi giao hàng, "ok", sticker, nội bộ) không trả lời.
      </p>
      <div>
        <Label className="text-xs">Khung giờ trợ lý trả lời (bấm T2…CN để chọn ngày áp dụng)</Label>
        <WindowsEditor value={f.replyWindows} onChange={(replyWindows) => set({ replyWindows })} label="Khung giờ" />
        <p className="text-xs text-muted-foreground">NV chỉnh được khung riêng cho nick của mình nhưng chỉ trong các khung này.</p>
      </div>
      <div>
        <Label className="text-xs">Số gọi gấp</Label>
        <Input value={f.hotline} maxLength={100} placeholder="0937.664.114 Ms.Hiền - 0933.160.169 Ms.Tiến" onChange={(e) => set({ hotline: e.target.value })} />
        <p className="text-xs text-muted-foreground">Nhiều số cách nhau " - " hoặc dấu phẩy: mỗi khách trợ lý chọn ngẫu nhiên 1 số (giữ số đó cho khách ấy).</p>
      </div>
      <div>
        <Label className="text-xs">Tên Zalo nội bộ (trợ lý không trả lời), cách nhau dấu phẩy</Label>
        <Input
          value={kw}
          maxLength={600}
          placeholder="minh tiến, pkd"
          onChange={(e) => {
            setKw(e.target.value);
            set({ internalNameKeywords: [...new Set(e.target.value.split(',').map((x) => x.trim()).filter((x) => x.length >= 2))].slice(0, 20) });
          }}
        />
        <p className="text-xs text-muted-foreground">
          Trợ lý cũng bỏ qua nhóm Zalo, nick công ty đã kết nối ERP và hội thoại NV đánh dấu &quot;Nội bộ&quot; (menu … của hội thoại).
        </p>
      </div>
      <div>
        <Label className="text-xs">Sổ tay của công ty cho trợ lý (cách khách hay gọi hàng, lưu ý riêng)</Label>
        <Textarea
          rows={3}
          maxLength={4000}
          value={f.skillNotes}
          placeholder='VD: "ống kẽm" là ống thép mạ kẽm; khách hỏi "BC" là bình chữa cháy.'
          onChange={(e) => set({ skillNotes: e.target.value })}
          className="text-xs"
        />
      </div>
      <label className="flex flex-wrap items-center gap-1.5 text-sm">
        Gọi AI tối đa
        <Input
          type="number"
          className="inline-block h-7 w-24 px-2 py-0 text-sm"
          value={f.aiDailyLimit}
          min={0}
          max={5000}
          onChange={(e) => set({ aiDailyLimit: Math.max(0, Math.min(5000, Math.round(Number(e.target.value) || 0))) })}
          aria-label="Số lần gọi AI tối đa mỗi ngày"
        />
        lần / ngày
        <span className="block w-full text-xs text-muted-foreground">
          Máy tự đọc hàng, tính giá, soạn câu (0 đ). AI (~100 đ / lần) chỉ chạy khi máy không hiểu tin. Đặt 0 = không dùng AI.
        </span>
      </label>
      <label className="flex items-start gap-1.5 text-sm">
        <input type="checkbox" className="mt-1" checked={f.autoQuote} disabled={!f.enabled} onChange={(e) => set({ autoQuote: e.target.checked })} />
        <span>
          Tự gửi báo giá tham khảo khi tổng chưa VAT ≤{' '}
          <Input
            type="number"
            className="inline-block h-7 w-32 px-2 py-0 text-sm"
            value={f.maxAutoTotal}
            min={0}
            step={500000}
            onChange={(e) => set({ maxAutoTotal: Math.max(0, Math.round(Number(e.target.value) || 0)) })}
            aria-label="Mốc tự gửi báo giá"
          />{' '}
          đ<span className="block text-xs text-muted-foreground">Lớn hơn mốc, thiếu mã / số lượng / giá thì báo giá thành nháp để NV gửi trong giờ làm.</span>
        </span>
      </label>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-xs">Bắt đầu làm việc</Label>
          <Input type="time" value={f.workStart} onChange={(e) => set({ workStart: e.target.value })} />
        </div>
        <div>
          <Label className="text-xs">Kết thúc</Label>
          <Input type="time" value={f.workEnd} onChange={(e) => set({ workEnd: e.target.value })} />
        </div>
      </div>
      <div className="flex flex-wrap gap-1">
        {DAYS.map(([d, l]) => (
          <button
            key={d}
            type="button"
            onClick={() => set({ workDays: toggle(f.workDays, d) })}
            className={`rounded-md border px-2 py-0.5 text-xs ${f.workDays.includes(d) ? 'border-primary bg-primary/10 text-primary' : 'text-muted-foreground'}`}
            aria-pressed={f.workDays.includes(d)}
          >
            {l}
          </button>
        ))}
        <span className="self-center text-xs text-muted-foreground">ngày làm việc</span>
      </div>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
        <span className="text-muted-foreground">Trả lời trên:</span>
        {(Object.keys(CHANNEL_LABELS) as InboxChannel[])
          .filter((c) => c !== 'email' && c !== 'phone')
          .map((c) => (
            <label key={c} className="flex items-center gap-1">
              <input type="checkbox" checked={f.channels.includes(c)} onChange={() => set({ channels: toggle(f.channels, c) })} /> {CHANNEL_LABELS[c]}
            </label>
          ))}
      </div>
      <label className="flex flex-wrap items-center gap-1.5 border-t pt-2.5 text-sm">
        <input type="checkbox" checked={f.summaryEnabled} onChange={(e) => set({ summaryEnabled: e.target.checked })} />
        Gửi tóm tắt công việc cuối ngày lúc
        <Input
          type="time"
          className="inline-block h-7 w-28 px-2 py-0 text-sm"
          value={f.summaryTime}
          disabled={!f.summaryEnabled}
          onChange={(e) => set({ summaryTime: e.target.value })}
          aria-label="Giờ gửi tóm tắt"
        />
        <span className="block w-full text-xs text-muted-foreground">
          Vào ngày làm việc ở trên. NV nhận phần của mình, TP kèm NV trong phòng, GĐ kèm cả công ty.
        </span>
      </label>
      <div className="flex items-center justify-between gap-2">
        <span className="text-[11px] text-muted-foreground">{f.updatedByName ? `Sửa lần cuối: ${f.updatedByName}` : 'Mặc định: tắt'}</span>
        <Button size="sm" disabled={save.isPending} onClick={() => save.mutate(f)}>
          Lưu
        </Button>
      </div>
    </div>
  );
}

/** Khung giờ trợ lý của chính NV cho nick / kênh của mình (trong khung công ty). */
export function MyBotSection() {
  const qc = useQueryClient();
  const key = ['zalo', 'bot-my-settings'];
  const q = useQuery({ queryKey: key, queryFn: () => api.get<MyBotSettings>('/zalo/bot/my-settings') });
  const [own, setOwn] = useState<ReplyWindow[] | null>(null);
  const [enabled, setEnabled] = useState(true);
  const [tone, setTone] = useState('');
  const [tpl, setTpl] = useState<BotTemplates | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);
  useEffect(() => {
    if (!q.data) return;
    setOwn(q.data.windows);
    setEnabled(q.data.enabled);
    setTone(q.data.tonePrompt);
    setTpl(q.data.templates);
  }, [q.data]);
  const save = useMutation({
    mutationFn: () => api.put<MyBotSettings>('/zalo/bot/my-settings', { enabled, windows: own, tonePrompt: tone, templates: tpl }),
    onSuccess: (d) => {
      qc.setQueryData(key, d);
      setWarnings([]);
      toast.success('Đã lưu cài đặt trợ lý của bạn');
    },
    onError: (e) => toast.error(errMsg(e)),
  });
  const compose = useMutation({
    mutationFn: () => api.post<{ templates: BotTemplates; warnings: string[] }>('/zalo/bot/my-settings/tone', { prompt: tone }),
    onSuccess: (d) => {
      setTpl(d.templates);
      setWarnings(d.warnings);
      toast.success('Đã soạn câu mẫu, xem lại rồi bấm Lưu');
    },
    onError: (e) => toast.error(errMsg(e)),
  });
  if (!q.data) return null;
  const d = q.data;
  return (
    <div className="space-y-2 border-t pt-3">
      <div className="flex items-center justify-between gap-2">
        <span className="flex items-center gap-1.5 text-sm">
          <Bot className="size-4" /> Trợ lý cho nick của tôi
        </span>
        <label className="flex items-center gap-1.5 text-sm">
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} aria-label="Bật trợ lý cho nick của tôi" /> Bật
        </label>
      </div>
      <p className="text-xs text-muted-foreground">
        {d.companyEnabled ? `Khung công ty: ${windowsText(d.companyWindows)}.` : 'Công ty đang tắt trợ lý.'} Trợ lý đang trả lời cho bạn:{' '}
        {enabled ? windowsText(d.effective) : 'tắt'}.
      </p>
      <label className="flex items-center gap-1.5 text-xs">
        <input type="checkbox" checked={own === null} disabled={!enabled} onChange={(e) => setOwn(e.target.checked ? null : d.companyWindows)} /> Theo khung
        công ty
      </label>
      {own !== null && enabled && <WindowsEditor value={own} onChange={setOwn} label="Khung của tôi" />}
      <div className="space-y-1.5">
        <Label className="text-xs">Ngữ điệu của tôi (trợ lý nói thay bạn theo giọng này)</Label>
        <Textarea
          rows={2}
          value={tone}
          maxLength={1000}
          placeholder="VD: xưng em, gọi anh/chị, ngắn gọn, hay dạ vâng, kết bằng 'nha'"
          onChange={(e) => setTone(e.target.value)}
        />
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" disabled={compose.isPending || tone.trim().length < 5} onClick={() => compose.mutate()}>
            {compose.isPending ? <Spinner /> : <Bot className="size-3.5" />} Soạn câu theo ngữ điệu
          </Button>
          {tpl ? (
            <Button size="sm" variant="ghost" onClick={() => setTpl(null)}>
              Dùng câu mẫu chung
            </Button>
          ) : (
            <Button size="sm" variant="ghost" onClick={() => setTpl({ ...d.defaultTemplates })}>
              Tự sửa câu mẫu
            </Button>
          )}
        </div>
        {warnings.map((w) => (
          <p key={w} className="text-xs text-warning">
            {w}
          </p>
        ))}
        {tpl ? (
          TEMPLATE_KINDS.map((k) => (
            <div key={k}>
              <Label className="text-xs text-muted-foreground">{d.labels[k]}</Label>
              <Textarea rows={2} value={tpl[k]} maxLength={300} onChange={(e) => setTpl({ ...tpl, [k]: e.target.value })} />
            </div>
          ))
        ) : (
          <p className="text-xs text-muted-foreground">Đang dùng câu mẫu chung: "{d.defaultTemplates.media}"</p>
        )}
        <p className="text-xs text-muted-foreground">
          Chỗ trống: {'{nv}'} tên bạn, {'{ten}'} tên gọi người xử lý, {'{khach}'} cách gọi khách, {'{gio}'} lúc bạn làm lại, {'{hotline}'} số gọi gấp. Luật cứng
          vẫn giữ: không biết thì im lặng, không tự ghi số / giá, không emoji, không hứa hàng hay giảm giá.
        </p>
      </div>
      <div className="flex justify-end">
        <Button size="sm" disabled={save.isPending} onClick={() => save.mutate()}>
          Lưu
        </Button>
      </div>
    </div>
  );
}

/** Nháp báo giá trợ lý soạn ngoài giờ, chờ NV sửa và gửi (hiện trên ô soạn tin). */
export function BotDraftBar({ conversationId }: { conversationId: number }) {
  const qc = useQueryClient();
  const key = ['zalo', 'bot-replies', conversationId];
  const q = useQuery({ queryKey: key, queryFn: () => api.get<{ items: BotReply[] }>(`/zalo/conversations/${conversationId}/bot-replies`) });
  const draft = q.data?.items.find((r) => r.status === 'draft' || r.status === 'failed');
  const [text, setText] = useState('');
  const [open, setOpen] = useState(false);
  useEffect(() => {
    setText(draft?.text ?? '');
    setOpen(false);
  }, [draft?.id, draft?.text]);
  const done = (d: { items: BotReply[] }) => {
    qc.setQueryData(key, d);
    void qc.invalidateQueries({ queryKey: ['zalo', 'messages', conversationId] });
  };
  const send = useMutation({
    mutationFn: () => api.post<{ items: BotReply[] }>(`/zalo/bot-replies/${draft!.id}/send`, { text: text.trim() }),
    onSuccess: (d) => {
      done(d);
      toast.success('Đã gửi cho khách');
    },
    onError: (e) => toast.error(errMsg(e)),
  });
  const dismiss = useMutation({
    mutationFn: () => api.post<{ items: BotReply[] }>(`/zalo/bot-replies/${draft!.id}/dismiss`),
    onSuccess: done,
    onError: (e) => toast.error(errMsg(e)),
  });
  if (!draft) return null;
  return (
    <div className="rounded-lg border border-primary/40 bg-primary/5 p-2 text-xs">
      <div className="flex items-center gap-1.5">
        <Bot className="size-3.5 shrink-0 text-primary" />
        <span className="min-w-0 flex-1 truncate font-medium">
          {draft.status === 'failed' ? 'Trợ lý gửi lỗi, NV gửi lại' : 'Trợ lý soạn sẵn báo giá ngoài giờ'}
          {draft.total ? ` · ${draft.total.toLocaleString('vi-VN')} đ chưa VAT` : ''}
        </span>
        <button type="button" className="text-primary hover:underline" onClick={() => setOpen((v) => !v)}>
          {open ? 'Thu gọn' : 'Xem / sửa'}
        </button>
        <button
          type="button"
          className="rounded p-0.5 text-muted-foreground hover:bg-muted"
          onClick={() => dismiss.mutate()}
          aria-label="Bỏ nháp"
          title="Bỏ nháp"
        >
          <X className="size-3.5" />
        </button>
      </div>
      {(draft.reason || draft.error) && <p className="mt-1 text-muted-foreground">Ghi chú cho NV (khách không thấy): {draft.error ?? draft.reason}</p>}
      {open && (
        <div className="mt-2 space-y-2">
          <Textarea
            rows={8}
            value={text}
            onChange={(e) => setText(e.target.value)}
            maxLength={4000}
            aria-label="Nội dung nháp của trợ lý"
            className="text-xs"
          />
          <div className="flex justify-end">
            <Button size="sm" disabled={!text.trim() || send.isPending} onClick={() => send.mutate()}>
              <Send /> Gửi cho khách
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
